<script lang="ts">
  import { onMount } from "svelte";
  import { push, link } from "svelte-spa-router";
  import {
    listRooms,
    updateRoom,
    setRoomTeachers,
    setRoomStudents,
    listUsers,
    listDevices,
    listSchools,
    listAuthorities,
    setDeviceRoom,
    fetchRoomE2eeStatus,
    ApiError,
  } from "../lib/api";
  import Breadcrumb from "../lib/Breadcrumb.svelte";
  import UserLink from "../lib/UserLink.svelte";
  import { scopes } from "../lib/session.svelte";
  import type {
    AdminRoom,
    AdminRoomE2eeStatusResponse,
    AdminUser,
    Authority,
    DeviceListEntry,
    School,
  } from "@diy-avatar/shared/admin";

  export let params: { id?: string } = {};
  $: roomId = params.id ? parseInt(params.id, 10) : NaN;

  let room: AdminRoom | null = null;
  let users: AdminUser[] = [];
  let roomDevices: DeviceListEntry[] = [];
  let unassignedDevices: DeviceListEntry[] = [];
  let loading = false;
  let error: string | null = null;

  let editDescription = "";
  let editVisibility: "public" | "restricted" = "public";
  let editForbidInvisible = false;
  let editForceE2eeQr = false;
  let editForceE2ee = false;
  /** "" = inherit (null); otherwise "0".."23". */
  let editRotateHour = "";
  /** Parent (school/authority) forbid state, for the inherited indicator. */
  let parentForbids = false;
  let parentForbidSource: string | null = null;
  /** Parent (school/authority) force-QR state, for the inherited indicator. */
  let parentForcesQr = false;
  let parentForceQrSource: string | null = null;
  /** Parent (school/authority) force-E2EE state, for the inherited indicator. */
  let parentForcesE2ee = false;
  let parentForceE2eeSource: string | null = null;

  /**
   * Live (in-memory) room E2EE status. null until loaded; stays null when E2EE
   * is globally disabled (the endpoint returns 404 → we hide the panel).
   */
  let e2eeStatus: AdminRoomE2eeStatusResponse | null = null;

  $: canManage =
    !!room &&
    ($scopes.isAdmin ||
      $scopes.manageableSchoolIds.includes(room.school_id) ||
      $scopes.teacherSchoolIds.includes(room.school_id));

  function handleAuth(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      push("/");
      return true;
    }
    return false;
  }

  function schoolTeachers(): AdminUser[] {
    if (!room) return [];
    return users.filter((u) =>
      u.roles.some((r) => r.role === "teacher" && r.school_id === room!.school_id),
    );
  }
  function schoolStudents(): AdminUser[] {
    if (!room) return [];
    return users.filter((u) =>
      u.roles.some((r) => r.role === "student" && r.school_id === room!.school_id),
    );
  }

  async function load(id: number) {
    if (!Number.isFinite(id)) return;
    loading = true;
    try {
      const [rm, us, dv, sc, au] = await Promise.all([
        listRooms(),
        listUsers().catch(() => ({ users: [] as AdminUser[] })),
        listDevices().catch(() => ({ devices: [] as DeviceListEntry[] })),
        listSchools().catch(() => ({ schools: [] as School[] })),
        listAuthorities().catch(() => ({ authorities: [] as Authority[] })),
      ]);
      room = rm.rooms.find((r) => r.id === id) ?? null;
      users = us.users;
      editDescription = room?.description ?? "";
      editVisibility = room?.visibility ?? "public";
      editForbidInvisible = room?.forbid_invisible ?? false;
      editForceE2eeQr = room?.force_e2ee_qr ?? false;
      editForceE2ee = room?.force_e2ee ?? false;
      editRotateHour =
        room?.e2ee_rotate_hour == null ? "" : String(room.e2ee_rotate_hour);
      roomDevices = dv.devices.filter((d) => d.room_id === id);
      unassignedDevices = dv.devices.filter((d) => d.room_id == null);
      // Inherited indicator: forbidden by the parent school or its authority.
      const school = sc.schools.find((s) => s.id === room?.school_id) ?? null;
      const authority =
        au.authorities.find((a) => a.id === school?.authority_id) ?? null;
      if (school?.forbid_invisible) {
        parentForbids = true;
        parentForbidSource = `school${school.name ? ` (${school.name})` : ""}`;
      } else if (authority?.forbid_invisible) {
        parentForbids = true;
        parentForbidSource = `authority${authority.name ? ` (${authority.name})` : ""}`;
      } else {
        parentForbids = false;
        parentForbidSource = null;
      }
      if (school?.force_e2ee_qr) {
        parentForcesQr = true;
        parentForceQrSource = `school${school.name ? ` (${school.name})` : ""}`;
      } else if (authority?.force_e2ee_qr) {
        parentForcesQr = true;
        parentForceQrSource = `authority${authority.name ? ` (${authority.name})` : ""}`;
      } else {
        parentForcesQr = false;
        parentForceQrSource = null;
      }
      if (school?.force_e2ee) {
        parentForcesE2ee = true;
        parentForceE2eeSource = `school${school.name ? ` (${school.name})` : ""}`;
      } else if (authority?.force_e2ee) {
        parentForcesE2ee = true;
        parentForceE2eeSource = `authority${authority.name ? ` (${authority.name})` : ""}`;
      } else {
        parentForcesE2ee = false;
        parentForceE2eeSource = null;
      }
      // Live E2EE status (best-effort): 404 means E2EE is globally off → hide
      // the panel; any other failure also degrades to hidden (never blocks load).
      e2eeStatus = await fetchRoomE2eeStatus(id).catch(() => null);
      error = null;
    } catch (err) {
      if (handleAuth(err)) return;
      error = err instanceof Error ? err.message : "Could not load room.";
    } finally {
      loading = false;
    }
  }

  async function saveRoom() {
    if (!room) return;
    error = null;
    const rotateHour =
      editRotateHour === "" ? null : parseInt(editRotateHour, 10);
    if (
      rotateHour !== null &&
      (!Number.isInteger(rotateHour) || rotateHour < 0 || rotateHour > 23)
    ) {
      error = "rotation hour must be 0-23";
      return;
    }
    try {
      await updateRoom(room.id, {
        description: editDescription.trim() || null,
        visibility: editVisibility,
        forbid_invisible: editForbidInvisible,
        force_e2ee_qr: editForceE2eeQr,
        force_e2ee: editForceE2ee,
        e2ee_rotate_hour: rotateHour,
      });
      await load(room.id);
    } catch (err) {
      if (handleAuth(err)) return;
      error = err instanceof Error ? err.message : "save failed";
    }
  }

  async function toggleTeacher(email: string, checked: boolean) {
    if (!room) return;
    error = null;
    const next = checked
      ? [...room.teachers, email]
      : room.teachers.filter((e) => e !== email);
    try {
      await setRoomTeachers(room.id, next);
      await load(room.id);
    } catch (err) {
      if (handleAuth(err)) return;
      error = err instanceof Error ? err.message : "teachers failed";
    }
  }

  async function toggleStudent(email: string, checked: boolean) {
    if (!room) return;
    error = null;
    const next = checked
      ? [...room.students, email]
      : room.students.filter((e) => e !== email);
    try {
      await setRoomStudents(room.id, next);
      await load(room.id);
    } catch (err) {
      if (handleAuth(err)) return;
      error = err instanceof Error ? err.message : "students failed";
    }
  }

  async function attachDevice(label: string) {
    if (!room || !label) return;
    error = null;
    try {
      await setDeviceRoom(label, room.id);
      await load(room.id);
    } catch (err) {
      if (handleAuth(err)) return;
      error = err instanceof Error ? err.message : "attach failed";
    }
  }

  async function detachDevice(d: DeviceListEntry) {
    error = null;
    try {
      await setDeviceRoom(d.label, null);
      await load(roomId);
    } catch (err) {
      if (handleAuth(err)) return;
      error = err instanceof Error ? err.message : "detach failed";
    }
  }

  $: if (Number.isFinite(roomId)) void load(roomId);
  onMount(() => {});
</script>

<section>
  <Breadcrumb
    items={[
      { label: "Schools", href: "/schools" },
      { label: room?.school_name ?? "School", href: room ? `/schools/${room.school_id}` : "/schools" },
      { label: room?.name ?? "…" },
    ]}
  />
  <h1>{room?.name ?? "Room"}</h1>
  {#if room}
    <p class="meta">
      School:
      <a href="/schools/{room.school_id}" use:link>{room.school_name ?? room.school_id}</a>
    </p>
  {/if}
  {#if error}<p class="error" data-testid="error">{error}</p>{/if}

  {#if room}
    <div class="panel" data-testid="room-info">
      <h2>Details</h2>
      <p class="hint">
        A room may represent a class name — it need not be a physical room.
      </p>
      <div class="row">
        <label>
          Visibility
          <select bind:value={editVisibility} disabled={!canManage} data-testid="room-visibility">
            <option value="public">public</option>
            <option value="restricted">restricted</option>
          </select>
        </label>
        <label class="grow">
          Description
          <input type="text" bind:value={editDescription} disabled={!canManage} data-testid="room-description" />
        </label>
        {#if canManage}
          <button on:click={saveRoom} data-testid="room-save">Save</button>
        {/if}
      </div>
      <div class="row forbid">
        <label class="toggle">
          <input
            type="checkbox"
            bind:checked={editForbidInvisible}
            disabled={!canManage}
            data-testid="forbid-invisible"
          />
          Forbid invisible mode
        </label>
        {#if parentForbids}
          <span class="inherited" data-testid="forbid-invisible-inherited">
            Already forbidden by {parentForbidSource}
          </span>
        {/if}
      </div>
      <div class="row forbid">
        <label class="toggle">
          <input
            type="checkbox"
            bind:checked={editForceE2eeQr}
            disabled={!canManage}
            data-testid="force-e2ee-qr"
          />
          QR-Schlüsselaustausch erzwingen
        </label>
        {#if parentForcesQr}
          <span class="inherited" data-testid="force-e2ee-qr-inherited">
            Bereits erzwungen durch {parentForceQrSource}
          </span>
        {/if}
      </div>
      <p class="hint e2ee-qr-desc">
        <strong>QR-Schlüsselaustausch erzwingen.</strong> Wenn aktiv, müssen
        Schüler:innen den QR-Code am Klassen-Display <strong>persönlich
        scannen</strong>. Der Schlüssel verlässt dabei nie unsere Server —
        höchste Sicherheit, aber alle müssen physisch anwesend sein. Wenn
        deaktiviert, werden Schlüssel automatisch verteilt (für jedes
        Schüler-Gerät versiegelt), sodass auch entfernte Schüler:innen ohne
        Scannen verschlüsselt teilnehmen — das vertraut darauf, dass der Server
        korrekte öffentliche Schlüssel ausliefert. Aktivierung auf
        <strong>einer beliebigen Ebene</strong> (Schulamt/Schule/Raum/Gerät)
        erzwingt QR für <strong>alle</strong> darunterliegenden Geräte und
        Schüler:innen.
      </p>
      <div class="row forbid">
        <label class="toggle">
          <input
            type="checkbox"
            bind:checked={editForceE2ee}
            disabled={!canManage}
            data-testid="force-e2ee"
          />
          Verschlüsselung erzwingen
        </label>
        {#if parentForcesE2ee}
          <span class="inherited" data-testid="force-e2ee-inherited">
            Bereits erzwungen durch {parentForceE2eeSource}
          </span>
        {/if}
      </div>
      <p class="hint e2ee-desc">
        <strong>Verschlüsselung erzwingen.</strong> Wenn aktiv, ist
        Ende-zu-Ende-Verschlüsselung für alle Geräte und Schüler:innen darunter
        verpflichtend (kein Opt-out, nicht unterstützte Browser werden
        blockiert). Schlüssel werden weiterhin <strong>automatisch verteilt</strong>
        (für jedes Schüler-Gerät versiegelt) — anders als „QR erzwingen“, das die
        automatische Verteilung abschaltet. Aktivierung auf
        <strong>einer beliebigen Ebene</strong> (Schulamt/Schule/Raum/Gerät)
        erzwingt Verschlüsselung für <strong>alle</strong> darunterliegenden
        Geräte und Schüler:innen.
      </p>
      <div class="row forbid">
        <label class="field">
          <span>Tägliche E2EE-Schlüsselrotation (Stunde, Ortszeit)</span>
          <select
            bind:value={editRotateHour}
            disabled={!canManage}
            data-testid="e2ee-rotate-hour"
          >
            <option value="">Vererbt (Standard)</option>
            {#each Array(24) as _, h}
              <option value={String(h)}>{String(h).padStart(2, "0")}:00</option>
            {/each}
          </select>
        </label>
      </div>
      <p class="hint">
        Stunde (0-23, Ortszeit), zu der der E2EE-Medienschlüssel täglich rotiert
        wird. Leer = von der übergeordneten Ebene erben (Standard 00:00).
        Speichern mit „Save“ oben.
      </p>
    </div>

    {#if e2eeStatus}
      <div class="panel" data-testid="room-e2ee-status">
        <h2>Verschlüsselungs-Status (live)</h2>
        {#if e2eeStatus.encryption_mode === "plaintext"}
          <p class="e2ee-mode plaintext" data-testid="e2ee-mode">
            <strong>Klartext (Kompatibilität)</strong> — ein Teilnehmergerät kann
            nicht verschlüsseln, daher läuft diese Sitzung unverschlüsselt, bis
            das Gerät den Raum verlässt.
          </p>
        {:else}
          <p class="e2ee-mode encrypted" data-testid="e2ee-mode">
            <strong>Verschlüsselt</strong> — alle Teilnehmer dieser Sitzung
            verschlüsseln Ende-zu-Ende.
          </p>
        {/if}
        {#if e2eeStatus.last_autoseal_failed}
          <p class="e2ee-anomaly" data-testid="e2ee-autoseal-failed">
            <strong>Auto-Seal fehlgeschlagen</strong> — zuletzt
            {new Date(e2eeStatus.last_autoseal_failed.at).toLocaleString()}
            {#if e2eeStatus.last_autoseal_failed.user_email}
              ({e2eeStatus.last_autoseal_failed.user_email})
            {:else if e2eeStatus.last_autoseal_failed.user_id != null}
              (Nutzer #{e2eeStatus.last_autoseal_failed.user_id})
            {/if}.
            Ein fähiges Schülergerät hat nach dem Heilfenster keinen Schlüssel
            erhalten — dies sollte nicht passieren.
          </p>
        {/if}
        <p class="hint">
          Spiegelt die live ausgehandelte Raum-Verschlüsselung wider (im
          Speicher, nicht persistiert). Erzwungene Räume bleiben immer
          verschlüsselt.
        </p>
      </div>
    {/if}

    <div class="panel" data-testid="room-teachers">
      <h2>Teachers (manage)</h2>
      <div class="members">
        {#each schoolTeachers() as u (u.id)}
          <label class="member">
            <input
              type="checkbox"
              checked={room.teachers.includes(u.email)}
              disabled={!canManage}
              on:change={(e) => toggleTeacher(u.email, e.currentTarget.checked)}
            />
            <UserLink
              id={u.id}
              email={u.email}
              fullName={u.full_name}
              displayName={u.display_name}
              hasImage={u.has_profile_image}
              size={20}
            />
          </label>
        {/each}
        {#if schoolTeachers().length === 0}<span class="muted">no teachers in school</span>{/if}
      </div>
    </div>

    <div class="panel" data-testid="room-students">
      <h2>Students (watch)</h2>
      <div class="members">
        {#each schoolStudents() as u (u.id)}
          <span class="member">
            <input
              type="checkbox"
              id="room-student-{u.id}"
              checked={room.students.includes(u.email)}
              disabled={!canManage}
              on:change={(e) => toggleStudent(u.email, e.currentTarget.checked)}
            />
            <UserLink
              id={u.id}
              email={u.email}
              fullName={u.full_name}
              displayName={u.display_name}
              hasImage={u.has_profile_image}
              size={20}
            />
          </span>
        {/each}
        {#if schoolStudents().length === 0}<span class="muted">no students in school</span>{/if}
      </div>
    </div>

    <div class="panel" data-testid="room-devices">
      <h2>Devices in this room</h2>
      <table>
        <thead><tr><th>id</th><th>label</th><th>online</th><th></th></tr></thead>
        <tbody>
          {#each roomDevices as d (d.label)}
            <tr data-testid="room-device-{d.label}">
              <td>{d.label}</td>
              <td><a href="/devices/{encodeURIComponent(d.label)}" use:link>{d.label}</a></td>
              <td><span class="dot" class:online={d.online}></span></td>
              <td>{#if canManage}<button on:click={() => detachDevice(d)}>remove</button>{/if}</td>
            </tr>
          {/each}
          {#if roomDevices.length === 0}
            <tr><td colspan="4" class="empty">No devices in this room.</td></tr>
          {/if}
        </tbody>
      </table>
      {#if canManage && unassignedDevices.length}
        <div class="row attach">
          <select data-testid="attach-device" on:change={(e) => { attachDevice(e.currentTarget.value); e.currentTarget.value = ""; }}>
            <option value="">— attach unassigned device —</option>
            {#each unassignedDevices as d (d.label)}
              <option value={d.label}>{d.label}</option>
            {/each}
          </select>
        </div>
      {/if}
    </div>
  {:else if !loading}
    <p class="error">Room not found.</p>
  {/if}
</section>

<style>
  h1 { margin: 0 0 0.3rem; }
  h2 { margin: 0 0 0.4rem; font-size: 1rem; color: #34d399; }
  .meta { color: #9ca3af; font-size: 0.85rem; margin: 0 0 0.75rem; }
  .meta a { color: #34d399; text-decoration: none; }
  .hint { color: #9ca3af; font-size: 0.8rem; margin: 0 0 0.5rem; }
  .panel {
    margin-bottom: 1rem;
    padding: 0.75rem 1rem;
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
  }
  .row { display: flex; gap: 0.5rem; flex-wrap: wrap; align-items: flex-end; }
  .row.attach { margin-top: 0.6rem; }
  .row.forbid { margin-top: 0.6rem; align-items: center; }
  .toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: #d1d5db;
    font-size: 0.85rem;
  }
  .toggle input { padding: 0; }
  .field {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    color: #d1d5db;
    font-size: 0.85rem;
  }
  .field select { max-width: 16rem; }
  .inherited { color: #fbbf24; font-size: 0.78rem; }
  .e2ee-qr-desc {
    color: #d1d5db;
    background: #1c1407;
    border-left: 3px solid #b45309;
    padding: 0.6rem 0.8rem;
    border-radius: 4px;
    line-height: 1.4;
  }
  .e2ee-qr-desc strong { color: #fbbf24; }
  .e2ee-mode {
    font-size: 0.85rem;
    line-height: 1.4;
    padding: 0.6rem 0.8rem;
    border-radius: 4px;
    margin: 0 0 0.5rem;
  }
  .e2ee-mode.encrypted {
    color: #d1fae5;
    background: #04261b;
    border-left: 3px solid #10b981;
  }
  .e2ee-mode.encrypted strong { color: #34d399; }
  .e2ee-mode.plaintext {
    color: #fee2e2;
    background: #2a0f0f;
    border-left: 3px solid #ef4444;
  }
  .e2ee-mode.plaintext strong { color: #f87171; }
  .e2ee-anomaly {
    font-size: 0.82rem;
    line-height: 1.4;
    color: #fde68a;
    background: #1c1407;
    border-left: 3px solid #b45309;
    padding: 0.6rem 0.8rem;
    border-radius: 4px;
    margin: 0 0 0.5rem;
  }
  .e2ee-anomaly strong { color: #fbbf24; }
  label { display: grid; gap: 0.2rem; font-size: 0.8rem; color: #9ca3af; }
  label.grow { flex: 1; min-width: 200px; }
  input, select {
    padding: 0.4rem 0.5rem;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
  }
  button {
    padding: 0.4rem 0.7rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 4px;
  }
  table { width: 100%; border-collapse: collapse; }
  th, td {
    padding: 0.35rem 0.5rem;
    text-align: left;
    border-bottom: 1px solid #1f2937;
    font-size: 0.85rem;
  }
  th { color: #9ca3af; }
  td a { color: #34d399; text-decoration: none; }
  .members { display: flex; flex-wrap: wrap; gap: 0.5rem; }
  .member { display: inline-flex; align-items: center; gap: 0.25rem; color: #d1d5db; font-size: 0.85rem; }
  .member input { padding: 0; }
  .muted { color: #6b7280; }
  .dot { display: inline-block; width: 0.6rem; height: 0.6rem; border-radius: 50%; background: #6b7280; }
  .dot.online { background: #10b981; }
  .empty { color: #6b7280; text-align: center; }
  .error { color: #fca5a5; }
</style>
